import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Autocomplete,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormGroup,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import FolderIcon from '@mui/icons-material/Folder';
import MovieIcon from '@mui/icons-material/Movie';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

// Deliverable nummer options (01-50)
const deliverableNumbers = Array.from({ length: 50 }, (_, i) => 
  String(i + 1).padStart(2, '0')
);

// Type deadline options with colors
const deadlineTypes = [
  { value: 'Hard', label: 'Hard', color: '#f97316', description: 'Vaste deadline die niet kan verschuiven' },
  { value: 'Klant', label: 'Klant', color: '#fb923c', description: 'Deadline afgesproken met klant' },
  { value: 'Zacht', label: 'Zacht', color: '#14b8a6', description: 'Flexibele deadline' },
  { value: 'Intern', label: 'Intern', color: '#86efac', description: 'Interne planning deadline' },
];

// Categorie options (replaces Status) with colors
const categorieOptions = [
  { value: 'Video/Film', label: 'Video/Film', color: '#dbeafe', textColor: '#1e40af' },
  { value: 'Audio', label: 'Audio', color: '#fef3c7', textColor: '#92400e' },
  { value: 'Animatie', label: 'Animatie', color: '#fce7f3', textColor: '#9d174d' },
  { value: 'Illustratie', label: 'Illustratie', color: '#fef9c3', textColor: '#854d0e' },
  { value: 'Document', label: 'Document', color: '#fef08a', textColor: '#713f12' },
  { value: 'Fotografie', label: 'Fotografie', color: '#e5e7eb', textColor: '#374151' },
  { value: 'Overig', label: 'Overig', color: '#f3f4f6', textColor: '#6b7280' },
];

// Format options
const formatOptions = [
  { value: '16:9', label: '16:9', description: 'Standaard breedbeeld' },
  { value: '4:3', label: '4:3', description: 'Klassiek formaat' },
  { value: '1:1', label: '1:1', description: 'Vierkant' },
  { value: '4:5', label: '4:5', description: 'Portret' },
  { value: '9:16', label: '9:16', description: 'Verticaal' },
  { value: 'Anders', label: 'Anders', description: 'Ander formaat' },
];

function CreateDeliverable() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { accounts } = useMsal();
  const userEmail = accounts?.[0]?.username || '';

  // Pre-fill from URL params
  const prefilledProjectId = searchParams.get('project');

  // Form state
  const [formData, setFormData] = useState({
    project: null,
    naam: '',
    nummer: '01',
    deadline: '',
    typeDeadline: 'Klant',
    categorie: 'Video/Film',
    formaten: ['16:9'],
    lengteVideo: '',
    opmerking: '',
    ondertiteling: false,
  });

  // Data state
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fetch initial data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch projects
      const projectsResponse = await authFetch(`${BACKEND_URL}/Api/projects`);
      if (projectsResponse.ok) {
        const data = await projectsResponse.json();
        const projectList = data.projects || data.records || [];
        setProjects(projectList);

        // Pre-select project if provided
        if (prefilledProjectId) {
          const project = projectList.find(p => p.id === prefilledProjectId);
          if (project) {
            setFormData(prev => ({ ...prev, project }));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Kon data niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handleFormatChange = (format) => {
    setFormData(prev => {
      const currentFormats = prev.formaten;
      if (currentFormats.includes(format)) {
        return { ...prev, formaten: currentFormats.filter(f => f !== format) };
      } else {
        return { ...prev, formaten: [...currentFormats, format] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.naam.trim()) {
      setError('Naam van deliverable is verplicht');
      return;
    }
    if (!formData.project) {
      setError('Selecteer een project');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const deliverableData = {
        'Naam van Deliverable': formData.naam,
        'Deliverable Nummer': formData.nummer,
        'Project': [formData.project.id],
        'TypeDeadline': formData.typeDeadline,
        'Categorie': formData.categorie,
        'Formaat': formData.formaten,
        'Deadline': formData.deadline || undefined,
        'LengteVideo': formData.lengteVideo ? parseInt(formData.lengteVideo) : undefined,
        'Ondertitel nodig': formData.ondertiteling,
      };

      const response = await authFetch(`${BACKEND_URL}/Api/deliverables`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(deliverableData),
      });

      if (!response.ok) {
        const errData = await response.json();
        console.error('[CreateDeliverable] Error:', errData);
        throw new Error(errData.details?.error?.message || errData.error || 'Deliverable aanmaken mislukt');
      }

      setSuccess(true);
      
      // Redirect after success
      setTimeout(() => {
        if (formData.project) {
          navigate(`/projects/${formData.project.id}`);
        } else {
          navigate('/dashboard');
        }
      }, 1500);

    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getProjectLabel = (project) => {
    const nr = project.fields?.Projectnummer || '';
    const name = project.fields?.Projectnaam || project.fields?.Projectnr_en_naam || '';
    return nr ? `${nr} - ${name}` : name;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Nieuwe deliverable aanmaken
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Via dit formulier kun je een deliverable aanmaken voor een project.
          </Typography>
        </Box>
      </Box>

      {/* User info */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon sx={{ fontSize: 18, color: '#6b7280' }} />
          <Typography variant="body2">{userEmail}</Typography>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Deliverable succesvol aangemaakt! Je wordt doorgestuurd...
        </Alert>
      )}

      <Paper sx={{ p: 4, border: '1px solid #e5e7eb' }} component="form" onSubmit={handleSubmit}>
        {/* Project Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Project <span style={{ color: '#ef4444' }}>*</span>
          </Typography>
          <Autocomplete
            value={formData.project}
            onChange={(_, newValue) => setFormData(prev => ({ ...prev, project: newValue }))}
            options={projects}
            getOptionLabel={getProjectLabel}
            renderInput={(params) => (
              <TextField {...params} placeholder="Selecteer een project" size="small" required />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FolderIcon sx={{ fontSize: 18, color: '#6b7280' }} />
                  {getProjectLabel(option)}
                </Box>
              </li>
            )}
          />
        </Box>

        {/* Naam van Deliverable */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Naam van Deliverable <span style={{ color: '#ef4444' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={formData.naam}
            onChange={(e) => setFormData(prev => ({ ...prev, naam: e.target.value }))}
            placeholder="Bijv. Brandmovie, TVC 30s, Social Cut-down"
            required
          />
        </Box>

        {/* Deliverable Nummer */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Deliverable Nummer
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Uniek nummer binnen het project (01-50)
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={formData.nummer}
              onChange={(e) => setFormData(prev => ({ ...prev, nummer: e.target.value }))}
            >
              {deliverableNumbers.map(num => (
                <MenuItem key={num} value={num}>{num}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Deadline */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Deadline
          </Typography>
          <TextField
            type="date"
            size="small"
            value={formData.deadline}
            onChange={(e) => setFormData(prev => ({ ...prev, deadline: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 200 }}
          />
        </Box>

        {/* Type Deadline */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Type Deadline
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              value={formData.typeDeadline}
              onChange={(e) => setFormData(prev => ({ ...prev, typeDeadline: e.target.value }))}
              row
            >
              {deadlineTypes.map((type) => (
                <FormControlLabel
                  key={type.value}
                  value={type.value}
                  control={<Radio size="small" />}
                  label={
                    <Chip
                      label={type.label}
                      size="small"
                      sx={{
                        backgroundColor: type.color,
                        color: '#fff',
                        fontWeight: 500,
                      }}
                    />
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>

        {/* Categorie */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Categorie <span style={{ color: '#ef4444' }}>*</span>
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Onder welke categorie valt deze deliverable?
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              value={formData.categorie}
              onChange={(e) => setFormData(prev => ({ ...prev, categorie: e.target.value }))}
            >
              {categorieOptions.map((cat) => (
                <FormControlLabel
                  key={cat.value}
                  value={cat.value}
                  control={<Radio size="small" />}
                  label={
                    <Chip
                      label={cat.label}
                      size="small"
                      sx={{
                        backgroundColor: cat.color,
                        color: cat.textColor,
                        fontWeight: 500,
                        minWidth: 90,
                      }}
                    />
                  }
                  sx={{ mb: 0.5 }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Formaat - Grid layout */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Formaat
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Selecteer één of meerdere formaten
          </Typography>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, 
            gap: 1.5 
          }}>
            {formatOptions.map((format) => {
              const isChecked = formData.formaten.includes(format.value);
              return (
                <Paper
                  key={format.value}
                  onClick={() => handleFormatChange(format.value)}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    border: isChecked ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                    backgroundColor: isChecked ? '#eff6ff' : '#fff',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    transition: 'all 0.15s',
                    '&:hover': {
                      borderColor: '#3b82f6',
                      backgroundColor: '#f8fafc',
                    },
                  }}
                >
                  <Checkbox
                    checked={isChecked}
                    size="small"
                    sx={{ p: 0, mt: 0.25 }}
                    onChange={() => {}}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{format.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{format.description}</Typography>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Box>

        {/* Lengte Video */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Lengte video (seconden)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Verwachte lengte van de video in seconden
          </Typography>
          <TextField
            type="number"
            size="small"
            value={formData.lengteVideo}
            onChange={(e) => setFormData(prev => ({ ...prev, lengteVideo: e.target.value }))}
            placeholder="bijv. 30, 60, 90"
            sx={{ width: 150 }}
            inputProps={{ min: 0 }}
          />
        </Box>

        {/* Ondertiteling */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.ondertiteling}
                onChange={(e) => setFormData(prev => ({ ...prev, ondertiteling: e.target.checked }))}
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Ondertiteling nodig
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Vink aan als deze deliverable ondertiteld moet worden
                </Typography>
              </Box>
            }
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Submit Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            disabled={submitting}
          >
            Annuleren
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <MovieIcon />}
            disabled={submitting}
            sx={{ borderRadius: 2 }}
          >
            {submitting ? 'Aanmaken...' : 'Deliverable aanmaken'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default CreateDeliverable;
